// Core
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

// Utils
import clsx from "clsx";

// Components
import Card from "@mui/material/Card";
import Drawer from "@mui/material/Drawer";
import Icon from "@mui/material/Icon";
import Box from "components/Atoms/Box";
import Tree, { useTreeState } from "react-hyper-tree";

// Context
import { useSoftUIController } from "context";

// Redux
import { selectAllMembers, useGetMembersByCommunityIdQuery } from "state/members/membersApiSlice";
import {
  selectAllResources,
  useGetResourcesByCommunityIdQuery,
} from "state/resources/resourcesApiSlice";

// Styles
import styles from "./styles";

function ComunityTree({ community }) {
  const [structure, setStructure] = useState({});
  const [controller, dispatch] = useSoftUIController();
  const { openConfigurator, sidenavColor } = controller;
  const classes = styles({ sidenavColor });

  // READ members
  const { members } = useGetMembersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ members: data && selectAllMembers(data) }),
    }
  );

  // Read resources
  const { resources } = useGetResourcesByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ resources: data && selectAllResources(data) }),
    }
  );

  useEffect(() => {
    if (members && resources) {
      const newStruct = members.map((member) => {
        const ownerships = member.ownerships?.map((ow) => ({
          id: ow.resourse_id,
          name: `${resources.filter((resource) => resource.id === ow.resource_id)[0]?.name} - ${
            ow.perc_ownership
          }%`,
        }));

        return { id: member.id, name: member.name, children: ownerships };
      });

      setStructure({
        id: 0,
        name: community.name,
        children: newStruct,
      });
    }
  }, [members, resources]);

  const { required, handlers } = useTreeState({
    data: structure || {},
    id: "your_tree_id",
    defaultOpened: true,
    multipleSelect: false,
  });

  const handleCloseConfigurator = () => {
    dispatch({ type: "OPEN_CONFIGURATOR", value: false });
  };

  return (
    <Drawer
      variant="permanent"
      classes={{
        paper: clsx(classes.configurator, {
          [classes.configurator_open]: openConfigurator,
          [classes.configurator_close]: !openConfigurator,
        }),
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={3}
        pb={0.8}
        px={3}
      >
        <Icon
          className={`font-bold ${classes.configurator_close_icon}`}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </Box>

      <Box my={3}>
        <Card>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
            {structure && <Tree {...required} {...handlers} />}
          </Box>
        </Card>
      </Box>
    </Drawer>
  );
}

ComunityTree.defaultProps = {};

ComunityTree.propTypes = {
  community: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.string,
    total_members: PropTypes.number,
    created_at: PropTypes.string,
  }).isRequired,
};

export default ComunityTree;
